import { ArrowUpRightIcon, EllipsisIcon, LockIcon, PlusIcon, SearchIcon } from "lucide-react"
import {
  createContext,
  use,
  useRef,
  useState,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react"
import { Link } from "react-router"
import { Button, buttonVariants } from "@/primitives/button.tsx"
import {
  Combobox,
  ComboboxFooter,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from "@/primitives/combobox.tsx"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/primitives/input-group.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/primitives/popover.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { shortcutOf, type ShortcutId } from "@/shortcuts/registry.ts"
import { useShortcut, useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { Kbd } from "./key.tsx"
import { PageSkeleton } from "./page-skeleton.tsx"
import { creatableFrom, groupsOf, isSearchable, matching, type PickerOption } from "./property.ts"
import { useTriggerTooltip } from "./use-trigger-tooltip.ts"
import { useViewport } from "./viewport.tsx"

export type { PickerOption } from "./property.ts"

/*
 * A thing's properties, each shown as its value and changed in place: a ticket's Status, priority,
 * type, assignees and Epic in its rail, and the Type and Priority chips of a composer. A property
 * is a quiet button showing its glyph and value; pressing it (or its key) opens a Picker of the
 * choices beneath it.
 *
 * The same properties draw as a rail of labelled rows or, inside a `PropertyBar`, as a row of
 * chips: a `PropertyRow` and its `PropertyButton` read which they are in from the context, so a
 * page builds its properties once and renders them once, each Picker binding its key once.
 */

/** Whether the properties around are a rail of labelled rows or a `PropertyBar`'s chips. */
const PropertyFormContext = createContext<"rail" | "bar">("rail")

/** The name of the property a `PropertyRow` holds, which its chip says in a `PropertyBar`. */
const PropertyLabelContext = createContext<string | undefined>(undefined)

/**
 * A ticket's properties as a row of chips under its title, as the compact layout shows them in
 * place of its rail: each `PropertyRow` inside is its button alone, drawn as a `PropertyChip` that
 * names its property to a screen reader, and they wrap onto as many lines as they need. What a
 * property's button warns of (an assignee who has lost Write) is written out on a line under them,
 * since touch shows no tooltip.
 */
export function PropertyBar({ children }: { children: ReactNode }) {
  return (
    <PropertyFormContext value="bar">
      <div role="group" aria-label="Properties" className="flex flex-wrap items-center gap-1.5">
        {children}
      </div>
    </PropertyFormContext>
  )
}

/** Where a Picker inside puts the focus as it closes, in place of its trigger. */
const PickerFocusReturnContext = createContext<(() => HTMLElement | null) | undefined>(undefined)

/**
 * Sends the focus to what `to` gives as each Picker inside closes, rather than back to its chip:
 * the field a docked Composer was being written in, so the on-screen keyboard comes back after a
 * pick. Giving nothing, the focus goes back to the chip.
 */
export function PickersReturnFocusTo({
  to,
  children,
}: {
  to: () => HTMLElement | null
  children: ReactNode
}) {
  return <PickerFocusReturnContext value={to}>{children}</PickerFocusReturnContext>
}

/**
 * The last chip of a `PropertyBar`, opening a sheet of the properties the bar has no chip for (a
 * ticket's Type), as labelled rows. `shortcut` is the key of the property it holds, which opens
 * it, since nothing in it is mounted to bind the key until it opens.
 *
 * Holding a value worth seeing at a glance, as a ticket's type, the chip says it: `holds` gives the
 * property and its value, the chip reads as the value ("Story") after its `glyph` (the ticket's
 * `TypeIcon`), and a screen reader hears "Type: Story, more properties". Without it the chip is
 * "More" after an ellipsis.
 */
export function MoreProperties({
  shortcut,
  glyph,
  holds,
  children,
}: {
  shortcut?: ShortcutId
  glyph?: ReactNode
  holds?: { label: string; value: string }
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  useShortcuts(shortcut ? { [shortcut]: () => setOpen(true) } : {})
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <PropertyChip
            glyph={
              glyph ?? (
                <EllipsisIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
              )
            }
            value={holds?.value ?? "More"}
            placeholder="More"
            {...(holds && { "aria-label": `${holds.label}: ${holds.value}, more properties` })}
          />
        }
      />
      <PopoverContent title="More" align="start" className="w-80">
        <PropertyFormContext value="rail">
          <div className="space-y-0.5">{children}</div>
        </PropertyFormContext>
      </PopoverContent>
    </Popover>
  )
}

/**
 * One labelled line of a property list: the label in a narrow column, then its button. The label
 * stays on the first line beside a `multiline` button that runs to more.
 *
 * `opens` is the page of what the value names, as a ticket's Epic, reached by an arrow after the
 * button that opens it in a new browser tab, so the page the property is on stays where it is; or,
 * where `in` says `in-place` (`compact`, and the installed app, which has no tabs), in its place,
 * with `state` for the page it opens. In a `PropertyBar` the arrow is a round button of its own
 * after the chip, kept on its line, since the chip's press opens its Picker.
 */
export function PropertyRow({
  label,
  opens,
  children,
}: {
  label: string
  opens?: { href: string; name: string; in?: "in-place" | "new-tab"; state?: unknown }
  children: ReactNode
}) {
  // In a bar, the row is its button alone, which names the property itself.
  if (use(PropertyFormContext) === "bar") {
    const chip = <PropertyLabelContext value={label}>{children}</PropertyLabelContext>
    if (!opens) return chip
    return (
      // The chip, a button that would otherwise keep its width, gives way to the arrow, cutting
      // its value short.
      <span className="flex max-w-full min-w-0 items-center gap-1 [&>:first-child]:shrink">
        {chip}
        <Link
          to={opens.href}
          {...linkOf(opens)}
          aria-label={openLabelOf(opens)}
          className={cn(
            buttonVariants({ variant: "outline", size: "icon-sm" }),
            "shrink-0 rounded-full shadow-none touch:size-9",
          )}
        >
          <ArrowUpRightIcon />
        </Link>
      </span>
    )
  }
  return (
    <div className="flex min-h-8 items-start gap-2">
      <span className="flex h-8 w-20 shrink-0 items-center text-xs text-muted-foreground">
        {label}
      </span>
      <div className="flex min-h-8 min-w-0 flex-1 items-center">{children}</div>
      {opens && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Link
                to={opens.href}
                {...linkOf(opens)}
                aria-label={openLabelOf(opens)}
                className={cn(buttonVariants({ variant: "ghost", size: "icon-sm" }), "mt-0.5")}
              />
            }
          >
            <ArrowUpRightIcon />
          </TooltipTrigger>
          <TooltipContent side="left">{openLabelOf(opens)}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

/** How a PropertyRow's arrow opens its page: in place, with its state, or in a new tab. */
const linkOf = (opens: { in?: "in-place" | "new-tab"; state?: unknown }) =>
  opens.in === "in-place"
    ? opens.state !== undefined && { state: opens.state }
    : { target: "_blank", rel: "noopener" }

/** What a PropertyRow's arrow says it does. */
const openLabelOf = (opens: { name: string; in?: "in-place" | "new-tab" }) =>
  opens.in === "in-place" ? `Open ${opens.name}` : `Open ${opens.name} in a new tab`

type PropertyButtonProps = {
  glyph?: ReactNode
  value?: string
  /** Shown faint in place of a value, as "No Epic" or "Nobody". */
  placeholder: string
  /**
   * The value runs onto as many lines as it needs, as the names of a ticket's Labels do, where
   * otherwise it is cut short at one.
   */
  multiline?: boolean
  readOnly?: boolean
  /** The key that opens its Picker; its tooltip names what the button does, and the key. */
  shortcut?: ShortcutId
  /** Something about the value to notice, as a dot with this in its tooltip. */
  warning?: string
  /**
   * What sets the value, when something else does and it can't be changed here, such as the Epic
   * that owns a ticket's Area, OPS-7: shown read only, with a lock and "from OPS-7" after it.
   */
  lockedFrom?: string
}

/*
 * The props a Picker's trigger hands its button (its ref, its handlers, its open state), which a
 * PropertyButton or PropertyChip passes on. Their look is theirs alone.
 */
type TriggerProps = Omit<
  ComponentProps<typeof Button>,
  "className" | "style" | "children" | "variant" | "size" | "render"
>

function Contents({
  glyph,
  value,
  placeholder,
  multiline = false,
  emptySays,
}: Pick<PropertyButtonProps, "glyph" | "value" | "placeholder" | "multiline"> & {
  /** Said to a screen reader after the placeholder, where it alone would say too little. */
  emptySays?: string
}) {
  return (
    <>
      {/* On several lines, the glyph stays level with the first. */}
      {multiline ? <span className="flex h-5 shrink-0 items-center">{glyph}</span> : glyph}
      {value ? (
        <span
          className={cn(
            "text-foreground",
            multiline ? "min-w-0 text-left break-words" : "truncate",
          )}
        >
          {value}
        </span>
      ) : (
        <span className="truncate text-subtle-foreground">
          {placeholder}
          {emptySays && <span className="sr-only">{emptySays}</span>}
        </span>
      )}
    </>
  )
}

const buttonClass = "h-8 w-full min-w-0 justify-start gap-2 px-2 text-ui font-normal"
const multilineClass = "h-auto min-h-8 items-start py-1.5 whitespace-normal"

/**
 * A property's value as a quiet, full-width button, the trigger of its Picker. Read only, it shows
 * the same thing as plain text, for a Member who can't change it. It is one line, cut short where
 * it doesn't fit, unless it is `multiline`. Inside a `PropertyBar` it is a `PropertyChip`, its
 * `warning` a line of text under the bar's chips.
 */
export function PropertyButton({
  glyph,
  value,
  placeholder,
  multiline = false,
  readOnly = false,
  shortcut,
  warning,
  lockedFrom,
  ...trigger
}: PropertyButtonProps & TriggerProps) {
  // Its Picker hands it `aria-expanded`, so the tooltip knows when the Picker is open.
  const expanded = trigger["aria-expanded"]
  const tip = useTriggerTooltip(expanded === true || expanded === "true")
  const form = use(PropertyFormContext)
  const label = use(PropertyLabelContext)
  if (form === "bar")
    return (
      <>
        <PropertyChip
          glyph={glyph}
          {...(value !== undefined && { value })}
          placeholder={placeholder}
          {...(label !== undefined && { label })}
          readOnly={readOnly}
          {...(lockedFrom !== undefined && { lockedFrom })}
          warned={warning !== undefined}
          {...trigger}
        />
        {warning && (
          // After every chip, on a line of its own, wherever the chip it is about stands.
          <p className="order-last flex basis-full items-center gap-1.5 text-xs text-muted-foreground">
            <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-warning" />
            {warning}
          </p>
        )}
      </>
    )
  const dot = warning && (
    <span
      role="img"
      aria-label={warning}
      className="ml-auto size-1.5 shrink-0 rounded-full bg-warning"
    />
  )
  if (readOnly || lockedFrom)
    return (
      <div
        className={cn(
          "flex w-full min-w-0 gap-2 px-2 text-ui",
          multiline ? "min-h-8 items-start py-1.5" : "h-8 items-center",
        )}
      >
        <Contents glyph={glyph} value={value} placeholder={placeholder} multiline={multiline} />
        {dot}
        {lockedFrom && (
          <span className="ml-auto flex shrink-0 items-center gap-1 text-xs text-subtle-foreground">
            <LockIcon aria-hidden className="size-3" />
            from {lockedFrom}
          </span>
        )}
      </div>
    )

  const contents = (
    <>
      <Contents glyph={glyph} value={value} placeholder={placeholder} multiline={multiline} />
      {dot}
    </>
  )
  const className = cn(buttonClass, multiline && multilineClass)
  const button = <Button variant="ghost" className={className} {...trigger} />
  if (!shortcut && !warning)
    return (
      <Button variant="ghost" className={className} {...trigger}>
        {contents}
      </Button>
    )
  return (
    <Tooltip {...tip.tooltip}>
      <TooltipTrigger render={button} {...tip.trigger}>
        {contents}
      </TooltipTrigger>
      <TooltipContent side="left">
        {warning && <span>{warning}</span>}
        {shortcut && (
          <>
            {warning && <span aria-hidden>·</span>}
            <span>{shortcutOf(shortcut).label}</span>
            <Kbd shortcut={shortcut} />
          </>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

const chipClass = "max-w-full min-w-0 rounded-full px-2.5 shadow-none"

/**
 * A property as a pill, for a composer's row of chips or a `PropertyBar`: its glyph and value,
 * opening its Picker, cut short within the width it is given. `label` names the property to a
 * screen reader ("Status: In progress"); with no value, the placeholder speaks for itself ("No
 * Epic", "Nobody"), and one that only names the property says it has none ("Size: none"). A glyph
 * that names itself, as an `AvatarStack` does, is `aria-hidden` by its caller, as the value says
 * it already. Read only, or `lockedFrom` what sets it, it is no button: a quiet pill on the muted
 * fill with no outline, as the rail writes a value it can't change as plain text, so only what can
 * be pressed looks pressable; a locked value says what sets it after a lock, "from OPS-7", as the
 * rail does. `warned` dots it, as its row's `PropertyButton` does.
 */
export function PropertyChip({
  glyph,
  value,
  placeholder,
  label,
  readOnly = false,
  lockedFrom,
  warned = false,
  ...trigger
}: Pick<PropertyButtonProps, "glyph" | "value" | "placeholder" | "lockedFrom"> & {
  label?: string
  readOnly?: boolean
  warned?: boolean
} & TriggerProps) {
  const contents = (
    <>
      {label && value && <span className="sr-only">{label}: </span>}
      <Contents
        glyph={glyph}
        value={value}
        placeholder={placeholder}
        {...(label === placeholder && { emptySays: ": none" })}
      />
      {lockedFrom && (
        <span className="flex shrink-0 items-center gap-1 text-xs text-subtle-foreground">
          <LockIcon aria-hidden className="size-3" />
          from {lockedFrom}
        </span>
      )}
      {warned && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-warning" />}
    </>
  )
  if (readOnly || lockedFrom)
    return (
      <span
        className={cn(
          chipClass,
          "inline-flex h-7 items-center gap-1.5 bg-muted text-ui whitespace-nowrap touch:min-h-9 [&_svg]:shrink-0",
        )}
      >
        {contents}
      </span>
    )
  return (
    <Button variant="outline" size="sm" className={chipClass} {...trigger}>
      {contents}
    </Button>
  )
}

const widths = {
  sm: "min-w-64",
  md: "w-80",
  lg: "w-md",
} as const

type Choice =
  | { mode: "single"; value: string | null; onChange: (id: string) => void }
  | { mode: "multi"; value: readonly string[]; onChange: (ids: string[]) => void }

/**
 * What a Picker can make from a name typed into its search: the words its row offers it in, and
 * what it says while there is nothing to choose from yet.
 */
const creations = {
  label: { offer: "Create label", hint: "Type a name to create a label" },
} as const

/** What a Picker with nothing to choose from at all says, by why it has nothing. */
const empties = {
  "no-repositories":
    "Beacon's GitHub App reaches no repositories yet. An org owner chooses them on GitHub.",
  "github-not-connected": "This stage has no GitHub App yet, so Beacon can't reach GitHub.",
  "github-unavailable": "GitHub couldn't be read just now. Try again in a moment.",
  "no-other-documents": "There's no other Document you can read to link to yet.",
} as const

/** The row that creates, among the choices' ids: no choice's id holds a NUL. */
const createId = "\u0000create"

/*
 * The choices for a property, in a popover under its trigger (`trigger`, usually a PropertyButton
 * or a PropertyChip). `title` is the property's name, as "Status" or "Time zone": on `compact`,
 * where the popover is a sheet, it is the sheet's title and accessible name. `single` picks one
 * and closes; `multi` checks and unchecks, staying open, with an optional sticky `footer` for the
 * caller's confirming action ("Add 3 Members"); without one, its sheet has Done beside its title.
 * On touch its search waits to be tapped before it takes the keyboard.
 *
 * Past seven choices it offers a search. A choice with a `disabledReason` can't be picked, and says
 * why. `shortcut` opens it from the keyboard; `open` and `onOpenChange` let the caller open or close
 * it too, as a footer's button does once it has acted. While `loading` (choices still being asked
 * for, as a rule's Slack channels are), skeleton rows stand where the choices will be.
 * `groupGlyph` leads each group's heading, as a provider's monogram leads its models. `onSubmit` is
 * what ⌘↵ does while the Picker is open, as the footer's button does, which then shows `Kbd
 * shortcut="submit"`.
 *
 * With `create`, it always has a search, and a name typed there that no choice is called is offered
 * as a last row, "Create label needs-design": choosing it hands the name to `onCreate` and empties
 * the search. What it `makes` holds the row's words.
 *
 * `empty` names what it says when there is nothing to choose from at all, such as why the choices
 * couldn't be read, in place of "No matches", which still answers a search that matches nothing.
 *
 * Closed, it puts the focus back on its trigger, or where a `PickersReturnFocusTo` around it says.
 */
export function Picker({
  title,
  options,
  searchPlaceholder,
  footer,
  width,
  trigger,
  shortcut,
  open: openProp,
  onOpenChange,
  loading = false,
  groupGlyph,
  onSubmit,
  create,
  empty,
  ...choice
}: Choice & {
  title: string
  options: readonly PickerOption[]
  searchPlaceholder: string
  footer?: ReactNode
  width: keyof typeof widths
  trigger: ReactElement
  shortcut?: ShortcutId
  open?: boolean
  onOpenChange?: (open: boolean) => void
  loading?: boolean
  groupGlyph?: (heading: string) => ReactNode
  onSubmit?: () => void
  create?: { makes: keyof typeof creations; onCreate: (name: string) => void }
  empty?: keyof typeof empties
}) {
  const { touch } = useViewport()
  const returnFocusTo = use(PickerFocusReturnContext)
  const [ownOpen, setOwnOpen] = useState(false)
  const open = openProp ?? ownOpen
  const [query, setQuery] = useState("")
  const list = useRef<HTMLDivElement>(null)
  const popup = useRef<HTMLDivElement>(null)
  const searchable = isSearchable(options) || create !== undefined
  const shown = searchable ? matching(options, query) : options
  const creatable = create ? creatableFrom(options, query) : undefined

  function setOpen(next: boolean) {
    if (openProp === undefined) setOwnOpen(next)
    onOpenChange?.(next)
    if (!next) setQuery("")
  }

  /** Makes the name typed, when the row offering it was the one chosen. */
  function created(chosen: readonly (string | null)[]) {
    if (!create || creatable === undefined || !chosen.includes(createId)) return false
    create.onCreate(creatable)
    setQuery("")
    return true
  }

  useShortcuts(shortcut ? { [shortcut]: () => setOpen(true) } : {})
  // The popover is open over the page, so ⌘↵ is bound within it, where the search has focus.
  useShortcut("submit", () => onSubmit?.(), { enabled: open && !!onSubmit, within: popup })

  // The combobox is the list inside the popover, always open while it is: its own popup is the
  // popover's. The search is the Picker's, filtered here, so an empty result is known.
  const shared = {
    inline: true,
    open,
    autoHighlight: true,
    filter: null,
    inputValue: query,
    onInputValueChange: (next: string, { reason }: { reason: string }) => {
      if (reason === "input-change") setQuery(next)
      else if (reason === "input-clear") setQuery("")
    },
  } as const

  const contents = (
    <>
      {searchable && <ComboboxInput variant="search" placeholder={searchPlaceholder} />}
      <ComboboxList ref={list} className="outline-none data-empty:p-1">
        {groupsOf(shown).map(({ heading, options }) => (
          <ComboboxGroup key={heading ?? ""}>
            {heading &&
              (groupGlyph ? (
                <ComboboxLabel className="flex items-center gap-1.5">
                  {groupGlyph(heading)}
                  {heading}
                </ComboboxLabel>
              ) : (
                <ComboboxLabel>{heading}</ComboboxLabel>
              ))}
            {options.map((option) => (
              <ComboboxItem
                key={option.id}
                value={option.id}
                disabled={option.disabledReason !== undefined}
                indicator={choice.mode === "multi" ? "checkbox" : "check"}
                className={cn(option.disabledReason && "h-auto min-h-8 py-1.5")}
              >
                {option.glyph}
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-baseline gap-1.5">
                    <span className="truncate">{option.label}</span>
                    {option.secondary && (
                      <span className="truncate text-subtle-foreground">{option.secondary}</span>
                    )}
                  </span>
                  {option.disabledReason && (
                    <span className="block text-xs text-muted-foreground">
                      {option.disabledReason}
                    </span>
                  )}
                </span>
                {option.trailing && (
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {option.trailing}
                  </span>
                )}
                {option.disabledReason && (
                  <LockIcon aria-hidden className="size-3.5 text-subtle-foreground" />
                )}
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        ))}
        {create && creatable !== undefined && (
          <ComboboxItem value={createId}>
            <PlusIcon aria-hidden className="size-3.5 text-subtle-foreground" />
            <span className="min-w-0 truncate">
              {creations[create.makes].offer} <span className="font-medium">{creatable}</span>
            </span>
          </ComboboxItem>
        )}
      </ComboboxList>
      {loading && <PageSkeleton kind="dialog-list" />}
      {!loading && shown.length === 0 && creatable === undefined && (
        <p className="px-4 py-6 text-center text-ui text-subtle-foreground">
          {options.length > 0
            ? "No matches"
            : create
              ? creations[create.makes].hint
              : empty
                ? empties[empty]
                : "No matches"}
        </p>
      )}
      {footer && <ComboboxFooter>{footer}</ComboboxFooter>}
    </>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger} />
      <PopoverContent
        ref={popup}
        title={title}
        // A sheet's multi-pick ends with Done beside its title, which a tap reaches.
        {...(choice.mode === "multi" &&
          !footer && {
            titleEnd: (
              <Button
                variant="ghost"
                size="sm"
                className="-mr-2 font-semibold"
                onClick={() => setOpen(false)}
              >
                Done
              </Button>
            ),
          })}
        align="start"
        // Without a search to type in, the list itself takes the arrow keys; so does it on touch,
        // where the search takes the keyboard only once it is tapped, so the keyboard doesn't rise
        // over the choices as they open.
        initialFocus={searchable && !touch ? true : list}
        // Closed, it gives the focus back to its trigger, unless a caller says where it goes.
        {...(returnFocusTo && { finalFocus: () => returnFocusTo() ?? true })}
        className={cn("gap-0 overflow-hidden p-0", widths[width])}
      >
        {choice.mode === "multi" ? (
          <Combobox
            {...shared}
            multiple
            value={[...choice.value]}
            onValueChange={(ids) => {
              if (!created(ids)) choice.onChange(ids)
            }}
          >
            {contents}
          </Combobox>
        ) : (
          <Combobox
            {...shared}
            value={choice.value}
            onValueChange={(id) => {
              if (id !== null && !created([id])) choice.onChange(id)
              setOpen(false)
            }}
          >
            {contents}
          </Combobox>
        )}
      </PopoverContent>
    </Popover>
  )
}

/*
 * The Picker's search row, for a menu that isn't a Combobox, as the Filter menu's list of a facet's
 * values isn't: a search icon, no chrome but a hairline beneath it, 36px tall. The Picker's own is
 * ComboboxInput's `search` shape, which can't stand outside a Combobox (base-ui's Combobox.Input
 * reads the root's context, and throws without it), so this draws the same row around a plain
 * input, class for class with that shape in `@/primitives/combobox.tsx`. Change one, change both.
 */
export function SearchRow(
  props: Omit<ComponentProps<typeof InputGroupInput>, "className" | "style">,
) {
  return (
    <InputGroup
      data-variant="search"
      className="h-9 w-full rounded-none border-0 border-b border-border-subtle bg-transparent px-1 shadow-none hover:border-border-subtle has-[[data-slot=input-group-control]:focus-visible]:border-border-subtle has-[[data-slot=input-group-control]:focus-visible]:ring-0 dark:bg-transparent"
    >
      <InputGroupAddon align="inline-start">
        <SearchIcon />
      </InputGroupAddon>
      <InputGroupInput {...props} />
    </InputGroup>
  )
}
